/**
 * Metabolic Vulnerability Index (MVX Plus) — proof-of-concept scoring model.
 *
 * These markers are NOT in the software yet (no Bloodwork fields, no DB
 * categories). This module is the self-contained "source of truth" that DEFINES
 * AND HOLDS the ranges for each MVX Plus marker so a gauge + pattern panel can be
 * designed against it. Pure functions, unit-tested independently, same split as
 * `computeI3` / `resolveThyroidPattern`. Inputs are a plain value bag (`MvxInputs`)
 * rather than `Bloodwork` so nothing needs wiring before the POC can run.
 *
 * TWO LAYERS, mirroring the thyroid model:
 *  1. The six MEASURED factors (GlycA, small HDL-P, citrate, valine, leucine,
 *     isoleucine) each get a held range → `markerState` (low/opt/high) and
 *     `markerZone` (green/yellow/orange/red). The factor states drive patterns.
 *  2. The three CALCULATED indices (MVX, IVX, MMX) are proprietary Labcorp NMR
 *     outputs — they CANNOT be recomputed from the six factors, so we HOLD them
 *     as entered values and only map them to a zone via their decision limits.
 *     MVX is the native 0–100 hero gauge; IVX/MMX are the two ranked sub-bars.
 *
 * PROVENANCE of the held numbers:
 *  • Lab-exact (Labcorp "Sample Report, 123601"): units (citrate mg/dL ⚠, the
 *    other five µmol/L); factor reference intervals (GlycA <400, small HDL-P
 *    11.5–21.6, citrate 1.08–2.73, valine 116–298, leucine 41–179, isoleucine
 *    28–109); index scale 1–100; and index decision limits, which DIFFER BY
 *    SPECIMEN TYPE. Labcorp accepts "plasma (preferred) or serum". The printed
 *    report's Reference Interval column is ALWAYS the EDTA-plasma one (MVX <63,
 *    IVX <59, MMX <71); the serum limits are stated only in its footnote, which
 *    publishes them outright: "FOR SERUM SAMPLES MVX SCORES WILL BE 5 POINTS
 *    LOWER, MMX SCORES WILL BE 15 POINTS LOWER AND IVX SCORES WILL BE 3 POINTS
 *    HIGHER. THEREFORE, THE REFERENCE INTERVALS (MEDICAL DECISION LIMITS) IN
 *    SERUM ARE AS FOLLOWS: MVX SCORE <58, MMX <56 AND IVX <62." Those are the
 *    numbers we hold — POINTS, published, not derived.
 *    ⚠ Labcorp's test-menu PAGE describes the same effect as a PERCENTAGE
 *    (MMX 10-15% higher in plasma, etc.), which does NOT reconcile with the
 *    report's points (15 points on MMX is ~21%). The two Labcorp sources
 *    disagree; the REPORT wins, because it states the decision limits
 *    explicitly and it is the document the practitioner is holding.
 *    `indexZone` defaults to plasma to match the printed report.
 *
 *    THE SERUM LIMITS ARE UNUSABLE IN PRACTICE, AND THAT IS SETTLED (2026-10-05).
 *    The report has no specimen-type field, only a specimen ID, and the
 *    practitioner never sees the tube: clients are sent to a Labcorp draw site
 *    and Labcorp chooses it. Nobody in the chain can say whether a given result
 *    is plasma or serum, so a specimen control would be a guess dressed as a
 *    setting — and a wrong guess silently moves MMX by 15 points. We therefore
 *    ALWAYS use the plasma limits, which is also exactly what the printed
 *    report does, so the panel and the document in the client's hand agree.
 *    DO NOT add a specimen toggle. `decisionLimitSerum` is kept only because
 *    Labcorp publishes it, and would become usable only if the specimen type
 *    ever arrives as data (e.g. on the Labcorp result feed, which carries
 *    fields the printed PDF does not).
 *  • Practitioner-optimal (Dr. Darren Schmidt) — the functional targets,
 *    tighter than the lab interval. These are his "MVX Plus Metabolic
 *    Resilience Targets" table (docs/reference/mvx-schmidt-slides-2026-09-20.md),
 *    taken from his 2026-09-20 talk and confirmed 2026-10-04 when he sent the
 *    same table again: GlycA 180–260, small HDL-P 19–23 (19–23 verbatim; its high lab/alarm bounds are raised past Labcorp to keep the bar's anchors ordered — see the marker's note), citrate 1.5–1.9, valine 210–250, leucine 130–170,
 *    isoleucine 45–65. Before that talk the bands came from BetterHealthGuy
 *    Ep. 216 (GlycA <300, citrate ≤2.0, BCAAs at the lab midpoints).
 *  • Tunable design choices: the `severe` (red) outer anchors and the index
 *    green/red margins — sensible clinical picks; adjust as validation accrues.
 *
 * Wording note: practitioner-facing names may be clinical; `clientTitle` on every
 * pattern is intentionally observational / non-diagnostic (no disease names, and
 * "reduced insulin sensitivity" is NEVER surfaced as "insulin resistance").
 */

/**
 * Dr. Darren Schmidt's MVX training, linked from the Insights panel.
 *
 * PRACTITIONER-ONLY. This is a paid professional program, so the link must
 * never reach a client's PDF — the panel suppresses it when `forPdf` is set.
 *
 * STILL NO MONEY BETWEEN THE TWO COMPANIES. No commission, no affiliate
 * arrangement, no revenue share: he links to us and we link to him. What
 * changed on 2026-10-13 is that HE offers a discount on HIS OWN program to
 * practitioners who come through here, which is his to give. Asked for a
 * tracking address he said there isn't one, and sent the plan link below with
 * a promo code in it.
 */
export const MVX_TRAINING = {
  // HIS TRACKING LINK, carrying our code. Sent by Dr. Schmidt on 2026-10-16,
  // confirmed live, and pasted here character for character. The Training tab
  // prints the same LABSMARTS50, so the button and the page agree.
  //
  // It replaced an earlier link of his carrying MVX50PROMO, which was his own
  // generic code. This one is ours, which is how he sees the traffic the tab
  // sends him: there is still no money moving in either direction.
  //
  // ⚠ The bundle_token makes this a gated per-offer URL rather than a stable
  // address, so it dies with the offer. His plain public address, for that
  // day: https://mvxtraining.com/
  url:
    'https://members.theholistichealthacademy.com/plans/1824323' +
    '?bundle_token=0bc3a6b5bebd1d60137196a096601847' +
    '&utm_source=manual&promo_code=LABSMARTS50',
  // The hook is RELEVANCE, not a generic endorsement: the green bands the
  // practitioner is reading on this very screen are his, which is a claim we
  // know first-hand to be true and no other software can make.
  //
  // "ranges and method", not "optimal ranges": he gives the targets, but also
  // which marker to start with, what each one points at and what to do about
  // it, and the panel is built on all of that (Maryann, 2026-10-09).
  //
  // Kept short enough to sit on ONE line in the 300px Insights column, where
  // it runs in front of "Train with him →" at 11px. That is about 57
  // characters for the pair, so the sentence has roughly 40 to work with.
  lead: 'Dr. Darren Schmidt’s ranges and method.',
  label: 'Train with Dr. Schmidt',
  // ⚠ HIS OWN PUBLIC CLAIM, from the back-story slide of his NEXGEN deck
  // ("Taught 10,000 practitioners in over 100 weekend seminars"), not
  // something we have verified. Get his nod before this ships — repeating a
  // partner's self-description in your own product is his reputation as much
  // as ours. His stronger claim, "the largest non-insurance nutrition clinic
  // in the U.S.", was deliberately left out: a superlative we cannot check.
  // Runs on from the disclaimer as one flowing paragraph rather than
  // stranded on a line of its own. "10,000+" over "more than 10,000" keeps
  // the pair inside two lines at this width.
  credential: 'He has trained 10,000+ practitioners.',
  // His offer, on his own program. The tab prints the "Use code" line only
  // while promoCode is non-empty, so clearing this one string retires the
  // offer everywhere.
  //
  // LABSMARTS50 is the code he is generating for us, and the tab prints it
  // from now (Maryann, 2026-10-15).
  //
  // Our code on his program, live since 2026-10-16. The link above carries
  // the same one, so the button and this line cannot disagree. Clearing this
  // string retires the printed offer everywhere.
  promoCode: 'LABSMARTS50',
  promoNote: '50% off your first month',
};

/** Labcorp MVX Plus (test #123601) test-page URL. */
export const LABCORP_MVX_PLUS_URL = 'https://www.labcorp.com/tests/123601/metabolic-vulnerability-index-mvx-plus-by-nmr';

/** Gauge zone for a value — matches the LabsmartsGauge / bar palette. */
export type MvxZone = 'green' | 'yellow' | 'orange' | 'red';

/** Per-marker state relative to its optimal band. 'absent' = not measured. */
export type MvxTri = 'low' | 'opt' | 'high' | 'absent';

/**
 * Specimen type — shifts the index decision limits (see header).
 *
 * Every caller leaves this at 'plasma' on purpose; see the header. It stays a
 * parameter rather than a constant so the published serum limits remain
 * expressible and unit-testable, not because the UI should ever choose.
 */
export type Specimen = 'plasma' | 'serum';

/**
 * How a marker's optimal band should be read:
 *  • 'lowerBetter'  — optimal is a ceiling; only being ABOVE it penalizes (GlycA).
 *  • 'higherBetter' — optimal is a floor; only being BELOW it penalizes (small HDL-P).
 *  • 'bandOptimal'  — optimal is a window; deviating EITHER way penalizes, and the
 *                     direction of the miss selects the pattern (citrate, BCAAs).
 */
export type MvxDirection = 'lowerBetter' | 'higherBetter' | 'bandOptimal';

export type MvxFactorId = 'glycA' | 'sHdlP' | 'citrate' | 'valine' | 'leucine' | 'isoleucine';
export type MvxIndexId = 'mvx' | 'ivx' | 'mmx';

/** A measured MVX factor and the ranges we hold for it. */
export interface MvxMarkerConfig {
  /** Stable key, also the future Bloodwork field id. */
  id: MvxFactorId;
  /** Sub-index this factor rolls up into. */
  index: 'IVX' | 'MMX';
  /** Display label. */
  label: string;
  /** Short label for the arrow / marker row. */
  short: string;
  /** Units of measure. NOTE: citrate is mg/dL; every other factor is µmol/L. */
  units: string;
  direction: MvxDirection;
  /**
   * The ONLY side of the band that can make this marker a "Start with" pick.
   * Omitted (the default, and true of five of the six factors) = either side.
   *
   * This is about ACTIONABILITY, not risk. A miss can be real, draw outside
   * the green on the bar and still be the wrong place to send a practitioner,
   * because triage answers "where do I start work?" and some directions have
   * no established starting move. Those misses still draw on the bar and still
   * print their value; they just do not get nominated.
   *
   * `reference`/`optimal` say where a value SITS. This says whether sitting
   * there tells anyone what to DO, which is a separate question and the reason
   * it is its own field rather than something inferable from the bands.
   */
  triageSide?: 'low' | 'high';
  /** Green band [lo, hi] — the optimal window (practitioner target). */
  optimal: [number, number];
  /**
   * The lowest value the bar draws. Below this the scale is dead space.
   *
   * GlycA only: Dr. Schmidt has never seen one below 100, and a bar drawn
   * from 0 spent a third of its green on values that do not occur. Same idea
   * as `floor` on the index config.
   *
   * DISPLAY ONLY. markerZone does not read it, and the DB range row keeps its
   * 0 low bounds, so a value below the floor is not newly flagged.
   */
  floor?: number;
  /**
   * A value inside the optimal band where the green starts giving way to
   * yellow on the bar, drawn as a gradient rather than a hard edge.
   *
   * GlycA only, and only because Dr. Schmidt asked for it (2026-10-08): the
   * band tops out at 300 but 260 is the number he wants people working toward.
   *
   * DISPLAY ONLY. It does not change markerZone, so a GlycA of 280 is still
   * green and still fires no bullet — see the glycA entry.
   */
  softenFrom?: number;
  /** Lab reference interval [lo, hi] (exact, per Sample Report 123601). */
  reference: [number, number];
  /** Outer red anchors [lo, hi] — beyond these = red (severe). Tunable. */
  severe: [number, number];
  /** One-line practitioner interpretation of an out-of-range result. */
  note: string;
}

/**
 * The six measured factors, with the ranges held per the provenance header.
 * `optimal`/`reference`/`severe` bounds are always written [low, high]; the
 * `direction` decides which side actually penalizes.
 */
/**
 * The alarm bounds for a factor, derived from its printed lab interval:
 * mean ± 3 SD.
 *
 * THE DEFAULT, set by Maryann on 2026-10-09. A printed 95% reference interval
 * is the mean plus or minus 2 SD, so the mean is its midpoint and the SD is a
 * quarter of its width; three of those is the alarm. Small HDL-P was already
 * built this way and the rest now follow it.
 *
 * What it REPLACED is worth remembering: citrate and leucine were set "just
 * outside the highest value on Labcorp's own abnormal sample report". That
 * sample is one printed example, not a reference — Maryann: "it was just a
 * random number LabCorp put on their abnormal sample, so it has nothing to do
 * with what our ranges are set at."
 *
 * Derived rather than typed so the bound cannot drift from the interval it
 * comes from. A bound Dr. Schmidt gave outright is written as a literal
 * instead, and says so.
 */
export function labAlarms([lo, hi]: [number, number], decimals = 2): [number, number] {
  const mean = (lo + hi) / 2;
  const sd = (hi - lo) / 4;
  const round = (n: number) => Math.round(n * 10 ** decimals) / 10 ** decimals;
  return [round(mean - 3 * sd), round(mean + 3 * sd)];
}

export const MVX_MARKERS: MvxMarkerConfig[] = [
  {
    id: 'glycA',
    index: 'IVX',
    label: 'GlycA',
    short: 'GlycA',
    units: 'µmol/L',
    direction: 'lowerBetter',
    // CHANGED 2026-10-08, from Dr. Schmidt directly: optimal now tops out at
    // 300 rather than 260, with 260 as the target. The bar paints
    // 260→300 as a gradient from green to yellowish (see softenFrom) so the
    // band does not claim 299 is as good as 259.
    //
    // ⚠ THIS LOOSENED THE INFLAMMATION BULLET. `markerZone` reads optimal[1],
    // so "Elevated inflammatory activity" now fires above 300 where it used to
    // fire above 260. A GlycA of 280 was flagged yesterday and is not today.
    // That follows from his own instruction (280 is inside the optimal band he
    // just described) but it is a clinical behaviour change, not a cosmetic
    // one, and it is the thing to confirm with him.
    optimal: [180, 300],
    softenFrom: 260,
    floor: 100,
    // Labcorp prints "<400" — STRICTLY less than — and markerZone compares
    // `value > refHi`, so 399 is the last in-range value. Holding 400 let a
    // GlycA of exactly 400 read in-range when the report would flag it. The
    // bar therefore ticks 399 where the printed report reads <400; same
    // interval, expressed for an inclusive bound.
    reference: [0, 399],
    // ALARM FROM 500. Dr. Schmidt, 2026-10-13, asked where alarm begins and
    // answered the question itself: "500 is really bad. That is the first
    // alarm. 700 is terrible." 700 was his earlier "really sick" figure and
    // had been held as the last non-alarm value; it is now well inside the
    // red rather than its edge. 499 is stored because markerZone compares
    // `value > sevHi`, so 500 is the first value that reads Alarm.
    severe: [0, 499],
    note: 'Elevated inflammatory / infection signal (tracks IL-6, CRP, fibrinogen); pursue infection + hypercoagulation work-up.',
  },
  {
    id: 'sHdlP',
    // RANGES REBUILT 2026-10-08, from Dr. Schmidt: the optimal ceiling comes
    // back to Labcorp's printed 21.6, and the two bounds above it are derived
    // from the printed interval exactly as the two below it already were.
    //
    //   11.5-21.6 printed  ->  mean 16.55, SD 2.525 (a 95% interval is +/-2SD)
    //   lab   = mean +/- 2SD = 11.5 and 21.6
    //   alarm = mean +/- 3SD =  9.0 and 24.1
    //
    // The high side now MIRRORS the low side, which it never did before. It
    // also retires two numbers that were only ever scaffolding: the 24.1 lab
    // ceiling invented to fit his old 23, and the mean+4SD alarm of 26.7 that
    // the References tab had to describe as "not a clinical threshold".
    //
    // Optimal max and lab max are the same number now, so the upper yellow
    // band has zero width. That is honest rather than awkward: Labcorp's
    // ceiling IS the top of both.
    index: 'IVX',
    label: 'Small HDL Particle Number',
    short: 'Small HDL-P',
    units: 'µmol/L',
    // CHANGED 2026-10-07 from 'higherBetter' to a true window, on the evidence.
    //
    // Harsløf M, Pedersen KM, Afzal S, et al. Cardiovasc Res 2023;119(4):957-968
    // (PMID 36537045, n=30,195, 15y follow-up) found a U SHAPE, not a ramp: the
    // lowest percentile carried HR 2.31 for any infection and 3.23 for
    // infection death, and the HIGHEST percentile still carried HR 1.36
    // (95% CI 1.07-1.74) for infection — small, but the interval excludes 1.
    // Their reference group was the 91st-95th percentile, i.e. the optimum sits
    // high in the distribution and short of the extreme, which is the shape of
    // a band. Gourgari E, et al. Cardiovasc Diabetol 2018;17(1):158
    // (PMID 30567548) found higher small HDL-P in type 1 diabetics than in
    // controls, correlating with WORSE cholesterol efflux.
    //
    // THE ASYMMETRY IS REAL AND IS NOT MODELLED HERE: low is 2-3x the risk,
    // high is ~1.36x for infection and shows NO mortality signal at all. A
    // symmetric bar therefore overstates the high side. It is still closer to
    // the evidence than the old 'never penalize high', which had no support
    // above roughly the 95th percentile. Revisit if the bar shape can grade
    // the two sides differently.
    direction: 'bandOptimal',
    // ONLY A LOW SMALL HDL-P IS A PLACE TO START (Maryann, 2026-10-22).
    //
    // The band penalizes both sides, and that is right for the BAR: a high
    // count is a real finding and should draw outside the green. It was wrong
    // for the triage line, which was nominating it as the one thing to work on
    // first. Dr. Schmidt on the 2026-10-19 call: the cause of a high small
    // HDL-P is not well established. There is no starting move to give.
    //
    // It also outranked markers that DO have one, because triage normalizes by
    // reference width and this interval is only 10.1 wide: a 21.7, a tenth of
    // a unit over, beat a GlycA past its own lab ceiling. And because the
    // optimal ceiling and the lab ceiling are the same 21.6 (see below), the
    // high side has no offTarget tier at all, so any overshoot jumped straight
    // to labAbnormal, the most urgent tier there is.
    //
    // The rest of the section already read it this way and this brings triage
    // into line: both pattern matchers test sHdlP === 'low', and all four of
    // its Blueprint triggers are when: 'low'. Nothing anywhere acts on high.
    // The asymmetry note under `direction` is the evidence: low is 2-3x the
    // risk, high is ~1.36x for infection with no mortality signal at all.
    triageSide: 'low',
    // Schmidt's band verbatim, 19–23 (Maryann's call, 2026-10-07).
    // Optimal Min 18 comes from her Marker Ranges tab (2026-10-08), down from
    // the 19 in Schmidt's original targets table. ⚠ It moves the Reduced
    // Detoxification Capacity bullet with it: a Small HDL-P of 18.5 fired
    // yesterday and does not today.
    // 21.6 IS THE TOP OF OPTIMAL, INCLUSIVE, and it is his number.
    //
    // Dr. Schmidt, asked whether 21.6 is the right top of the window:
    // "In the context of MVX Plus and the math to make IVX, 21.6 is a
    // ceiling." Read against his own targets table, which asks for 19-23,
    // that means the IVX scoring stops rewarding a higher count at 21.6:
    // pushing a patient from 21.6 to 23 buys nothing, which is why he is
    // content for the band to end where Labcorp's interval does. A ceiling is
    // the top of the good range, not the point where trouble starts.
    //
    // It was briefly 21.5, to stop the bar printing "21.6" twice where the
    // optimal and lab ceilings coincide. That was a drawing problem solved by
    // moving a clinical boundary, which is the wrong way round; the bar now
    // handles the coincidence itself (see MvxOptimalBar).
    optimal: [18, 21.6],
    reference: [11.5, 21.6],
    severe: labAlarms([11.5, 21.6], 1), // 9 and 24.1
    // THE HIGH SIDE IS DERIVED THE SAME WAY THE LOW SIDE ALWAYS WAS: both lab
    // bounds are Labcorp's printed interval, and both alarms are mean ± 3 SD
    // off it. An earlier pass held Dr. Schmidt's 19-23 instead, which forced
    // the lab ceiling up to 24.1 and the alarm to a mean+4SD 26.7 that the
    // References tab had to describe as "not a clinical threshold", because
    // the bar's grammar is optimal ⊂ lab ⊂ alarm and a 23 will not fit under
    // 21.6. Both of those invented numbers are gone.
    //
    // THE ASYMMETRY IS STILL NOT MODELLED. Low is 2-3x the risk; high is
    // ~1.36x for infection with no mortality signal at all, so a symmetric
    // bar overstates the high side. Revisit if the bar shape can ever grade
    // its two sides differently.
    note: 'Low = toxic burden / impaired detox (liver → mold/metals/chemicals); also consumed by active inflammation. High is weakly unfavorable (infection risk) with no mortality signal.',
  },
  {
    id: 'citrate',
    index: 'MMX',
    label: 'Citrate',
    short: 'Citrate',
    units: 'mg/dL', // ⚠ mg/dL, unlike the other five
    direction: 'bandOptimal',
    optimal: [1.5, 1.9], // Schmidt target 1.5–1.9 (mid-normal); "citrate can bounce around"
    reference: [1.08, 2.73], // lab-exact
    severe: labAlarms([1.08, 2.73]), // 0.67 and 3.14
    note: 'High = mitochondrial dysfunction / toxicity / nutrient (esp. B1) deficiency; low = thin Krebs-cycle substrate.',
  },
  {
    id: 'valine',
    index: 'MMX',
    label: 'Valine',
    short: 'Valine',
    units: 'µmol/L',
    direction: 'bandOptimal',
    optimal: [210, 250], // Schmidt target 210–250 (mid- to upper-normal)
    reference: [116, 298], // lab-exact
    // 400 from Dr. Schmidt, 2026-10-08, down from 470.
    //
    // It sits below the 405 on Labcorp's abnormal sample report, and that is
    // fine: Maryann, 2026-10-08, "it was just a random number LabCorp put on
    // their abnormal sample, so it has nothing to do with what our ranges are
    // set at." Worth keeping in mind for citrate and leucine, whose alarm
    // bounds are still justified as "just outside" that same sample.
    // Low derived (70.5), high given by Dr. Schmidt on 2026-10-08.
    severe: [labAlarms([116, 298])[0], 400],
    note: 'High = reduced insulin sensitivity / impaired BCAA catabolism; low = protein-energy malnutrition.',
  },
  {
    id: 'leucine',
    index: 'MMX',
    label: 'Leucine',
    short: 'Leucine',
    units: 'µmol/L',
    direction: 'bandOptimal',
    optimal: [130, 170], // Schmidt target 130–170 (upper-normal)
    reference: [41, 179], // lab-exact
    // 6.5 and 213.5. ⚠ The low bound is effectively unreachable: the printed
    // interval is 138 wide, so three SD below its midpoint lands far under
    // anything a living person measures. Leucine therefore has no working low
    // alarm, and a genuinely low value tops out at Elevated. Flagged rather
    // than fudged — the formula is the rule, and the fix is a figure from
    // Dr. Schmidt, the way he gave one for isoleucine.
    // LOW ALARM 40, from Dr. Schmidt on 2026-10-13 ("your suggestion is
    // good"). Mean − 3 SD off the printed 41-179 gives 6.5, which no living
    // person reaches, so leucine had no working low alarm at all: the
    // interval is 138 wide and three SD below its midpoint lands under
    // anything measurable. 40 is the rule he had already used on isoleucine,
    // whose alarm min of 27 sits one below its printed 28, so any leucine
    // under the lab range now reads Alarm exactly as isoleucine does.
    //
    // The high bound is still the arithmetic one, 213.5, which he confirmed
    // by name the same day.
    severe: [40, labAlarms([41, 179], 1)[1]], // 40 (his) and 213.5 (mean + 3 SD)
    note: 'High = reduced insulin sensitivity / impaired BCAA catabolism; low = protein-energy malnutrition.',
  },
  {
    id: 'isoleucine',
    index: 'MMX',
    label: 'Isoleucine',
    short: 'Isoleucine',
    units: 'µmol/L',
    direction: 'bandOptimal',
    optimal: [45, 65], // Schmidt target 45–65 (mid-normal)
    reference: [28, 109], // lab-exact
    // 27 and 150 from Dr. Schmidt, 2026-10-08. 150 is exactly the isoleucine
    // on Labcorp's abnormal sample report; markerZone compares `value > sevHi`,
    // so that sample still reads Elevated and 151 is the first Alarm.
    severe: [27, 150],
    note: 'High = reduced insulin sensitivity / impaired BCAA catabolism; low = protein-energy malnutrition.',
  },
];

/** A calculated index and the thresholds we hold for it. */
export interface MvxIndexConfig {
  id: MvxIndexId;
  label: string;
  short: string;
  /** Client-facing, non-diagnostic one-liner: what this score means to the patient. */
  client: string;
  /** Top of the score scale (all three are 1–100). */
  scaleMax: number;
  /** Practical floor of the score (MMX rarely drops below the mid-30s) — for BAR scaling. */
  floor: number;
  /**
   * Schmidt's observed extremes across his own cases [lowest, worst], from his
   * 2026-09-20 talk. Context for reading a score, not a threshold: every index
   * is "get it as low as you can"; IVX is good in the 20s–30s, MMX is best in
   * the high 40s. Worst is undefined where he gave no upper figure.
   */
  observed: [number, number | undefined];
  /**
   * Where the green band ends: a score BELOW this is optimal, so the last
   * green value is one less. Per index because the three have three different
   * sources — see the values. Specimen-independent: the one figure with a
   * study behind it (MVX) comes from a plasma cohort, and we read plasma.
   */
  greenThreshold: number;
  /**
   * A score inside the green band where it starts giving way to yellow on the
   * bar, drawn as a gradient rather than a hard edge. Same idea as the marker
   * field of the same name.
   *
   * MMX only: Dr. Schmidt, 2026-10-08, wants 45 read as the target
   * with 45↓49 progressively less good, rather than one flat green to 49.
   *
   * DISPLAY ONLY. indexZone still turns yellow at greenThreshold, so a score
   * of 47 is still green and still counts as optimal.
   */
  softenFrom?: number;
  /** Printed-report medical decision limit (EDTA plasma) — at/above = out of range. */
  decisionLimitPlasma: number;
  /**
   * Where the red band starts, when it is not the shared INDEX_RED_MARGIN.
   *
   * MMX only: Dr. Schmidt put it at 75 on 2026-10-08, which is far tighter
   * than the 15-point margin the other two use (71 + 15 = 86).
   *
   * 76 rather than 75 because her Marker Ranges tab records it in the Alarm
   * Max column, which holds the LAST NON-ALARM value everywhere else in that
   * sheet — MVX 77 with red starting at 78. So Alarm Max 75, red from 76.
   */
  alarmFrom?: number;
  /** Serum medical decision limit, as published in the report footnote. */
  decisionLimitSerum: number;
}

/**
 * Red starts this far above the decision limit. Still a LabSmarts display
 * margin, chosen rather than derived — unlike the green edge, which now has a
 * source per index (see `greenThreshold`).
 */
export const INDEX_RED_MARGIN = 15;

/**
 * The three indices. MVX is the hero gauge; IVX/MMX are the sub-bars. `floor`
 * matters for BAR scaling (draw MMX on ~36→60, not 0→100) — it does not affect
 * zones. These are proprietary NMR outputs: HELD from the report, never derived.
 * Schmidt (2026-09-20): treating MMX as a whole "could be more important than
 * addressing citrate, BCAAs individually".
 */
export const MVX_INDICES: MvxIndexConfig[] = [
  // greenThreshold — three indices, three different sources:
  //  MVX 40  CHANGED 2026-10-08. The Otvos figure, 34, moved from the green
  //          EDGE to the target (softenFrom), and the edge became 39 with
  //          the band fading between them. The sourced number is now where it
  //          belongs — 34 is the top of the lowest mortality stratum, i.e. the
  //          best place to be, not the last acceptable place — and the ceiling
  //          it used to serve as is a chosen bound like IVX's.
  //          ⚠ This widened the green zone on the HERO GAUGE too: an MVX of 37
  //          read Borderline yesterday and reads Optimal today.
  //  MMX 50  Schmidt: "best in the high 40's". So 49 is the last green value.
  //  IVX 40  the top of Schmidt's "good if in the 20's and 30's", so 39 is the
  //          last optimal score. The target is 25 (softenFrom): he says he
  //          LIKES it in the 20s, so the band fades from 25 to 39 rather than
  //          calling 38 as good as 24. Read the same way MMX reads his "high 40's"
  //          as 49: take the top of the range he names. CHANGED 2026-10-07
  //          from 39 (which made 38 the last optimal score) — that number came
  //          from "20 points below the decision limit", arithmetic LabSmarts
  //          invented and has since removed. Still OPEN with him: a range in
  //          speech is not a threshold, and he has never given an exact edge.
  { id: 'mvx', label: 'Metabolic Vulnerability Index', short: 'MVX', client: 'the overall metabolic-resilience score — the combined picture from the two sub-scores', scaleMax: 100, floor: 0, greenThreshold: 40, softenFrom: 34, decisionLimitPlasma: 63, decisionLimitSerum: 58, /* report footnote: 5 points lower */ observed: [13, 81] },
  { id: 'ivx', label: 'Inflammation Vulnerability Index', short: 'IVX', client: 'How much inflammation or hidden infection', scaleMax: 100, floor: 0, greenThreshold: 40, softenFrom: 25, decisionLimitPlasma: 59, decisionLimitSerum: 62, /* report footnote: 3 points HIGHER */ observed: [9, undefined] },
  // softenFrom 45: his target inside a band that runs to 49.
  //
  // floor 34, not the 36 he had never seen: "maybe bring mmx to 34, just
  // in case someone in the future has a new record score" (Dr. Schmidt,
  // 2026-10-13). Headroom under the lowest MMX he has met, so a new record
  // low has somewhere to plot instead of pinning to the left edge.
  { id: 'mmx', label: 'Metabolic Malnutrition Index', short: 'MMX', client: 'Whether the cells are well-nourished and well-fueled', scaleMax: 100, floor: 34, greenThreshold: 50, softenFrom: 45, alarmFrom: 76, decisionLimitPlasma: 71, decisionLimitSerum: 56, /* report footnote: 15 points lower */ observed: [36, undefined] },
];

// ─────────────────────────── factor scoring ───────────────────────────

/** Read one input value: `{ value, hasValue }`, blank/NaN → not entered. */
function readNumber(inputs: MvxInputs | undefined, id: string): { value: number; hasValue: boolean } {
  const raw = inputs ? (inputs as any)[id] : undefined;
  if (raw === undefined || raw === null || (raw as any) === '') return { value: 0, hasValue: false };
  const n = Number(raw);
  if (isNaN(n)) return { value: 0, hasValue: false };
  return { value: n, hasValue: true };
}

/** Classify a value against its optimal band, respecting the marker's direction. */
/**
 * The band a marker is JUDGED against, which is not always the band the bar
 * DRAWS.
 *
 * When a marker has a target inside its band, that is what it is judged on. Maryann,
 * 2026-10-08: "the bullets should still trigger at the optimal mean tick mark,
 * because that's really the most ideal solid green number." So GlycA draws
 * green out to 300 with a fade from 260, and is judged against 260.
 *
 * The two are deliberately different. The fade is the bar saying "you are
 * inside the range but drifting"; the bullet is the panel saying "this is
 * worth doing something about". A value in the fade gets both, which is the
 * intended reading rather than a contradiction.
 *
 * Markers with no target inside the band are unaffected: the band IS the target.
 */
export function targetBand(cfg: MvxMarkerConfig): [number, number] {
  const [lo, hi] = cfg.optimal;
  if (cfg.softenFrom === undefined) return [lo, hi];
  return cfg.direction === 'higherBetter' ? [cfg.softenFrom, hi] : [lo, cfg.softenFrom];
}

/** The last score an index counts as on target. Its target when it has one. */
export function indexTarget(cfg: MvxIndexConfig): number {
  return cfg.softenFrom ?? cfg.greenThreshold - 1;
}

export function markerState(value: number, cfg: MvxMarkerConfig): MvxTri {
  const [optLo, optHi] = targetBand(cfg);
  const penalizeHigh = cfg.direction !== 'higherBetter';
  const penalizeLow = cfg.direction !== 'lowerBetter';
  if (penalizeHigh && value > optHi) return 'high';
  if (penalizeLow && value < optLo) return 'low';
  return 'opt';
}

/**
 * Map a value to a gauge zone using the three held bands. Being on the "good"
 * side of the optimal window is always green (a low GlycA or a high small HDL-P
 * is never penalized); a miss on the penalized side steps green→yellow (past
 * optimal)→orange (past the lab reference)→red (past the severe anchor).
 */
export function markerZone(value: number, cfg: MvxMarkerConfig): MvxZone {
  // The TARGET band, not the drawn one — see targetBand. The bar's own colours
  // come from markerBar's segments, which still paint green to the band edge
  // with a fade across the stretch beyond the target.
  const [optLo, optHi] = targetBand(cfg);
  const [refLo, refHi] = cfg.reference;
  const [sevLo, sevHi] = cfg.severe;
  const penalizeHigh = cfg.direction !== 'higherBetter';
  const penalizeLow = cfg.direction !== 'lowerBetter';

  if (penalizeHigh && value > optHi) {
    if (value > sevHi) return 'red';
    if (value > refHi) return 'orange';
    return 'yellow';
  }
  if (penalizeLow && value < optLo) {
    if (value < sevLo) return 'red';
    if (value < refLo) return 'orange';
    return 'yellow';
  }
  return 'green';
}

// ─────────────────────────── index scoring ───────────────────────────

/** The specimen-specific medical decision limit for an index. */
export function indexLimit(cfg: MvxIndexConfig, specimen: Specimen = 'plasma'): number {
  return specimen === 'serum' ? cfg.decisionLimitSerum : cfg.decisionLimitPlasma;
}

/** Map a held index value to a zone via its (specimen-specific) decision limit. */
export function indexZone(value: number, cfg: MvxIndexConfig, specimen: Specimen = 'plasma'): MvxZone {
  const limit = indexLimit(cfg, specimen);
  // Green to the TARGET when there is one, so the gauge word and the bar
  // agree about where "optimal" stops meaning ideal. Without this, raising
  // MVX's band to 39 would have made a 37 read Optimal on the hero gauge.
  if (value <= indexTarget(cfg)) return 'green';
  if (value < limit) return 'yellow';
  if (value < (cfg.alarmFrom ?? limit + INDEX_RED_MARGIN)) return 'orange';
  return 'red';
}

// ─────────────────────── bar geometry (drives the panel bars) ───────────────────────

export interface MvxBarSegment {
  zone: MvxZone;
  from: number;
  to: number;
}
export interface MvxBarTick {
  value: number;
  /** 'opt' = an optimal-band edge, 'lab' = a lab-reference edge. */
  kind: 'opt' | 'lab';
}
export interface MvxBar {
  domLo: number;
  domHi: number;
  segments: MvxBarSegment[];
  ticks: MvxBarTick[];
}

/**
 * Zone-banded range bar for one factor, mirroring the app's MiniReferenceRangeChart:
 * green = optimal band, yellow shoulders = lab reference, orange = alarm, red =
 * severe. The domain auto-expands to keep an out-of-range `value` on-scale, and
 * `ticks` are the optimal edges ('opt') and lab-reference edges ('lab') to label.
 * Contiguous by construction: segment `from`s chain to the previous `to`, the
 * first starts at `domLo`, the last ends at `domHi`.
 */
export function markerBar(cfg: MvxMarkerConfig, value: number): MvxBar {
  const [optLo, optHi] = cfg.optimal;
  const [refLo, refHi] = cfg.reference;
  const [sevLo, sevHi] = cfg.severe;
  const seg = (zone: MvxZone, from: number, to: number): MvxBarSegment => ({ zone, from, to });

  if (cfg.direction === 'lowerBetter') {
    const domHi = Math.max(sevHi, value) * 1.03;
    // The bar starts at the marker's floor, not at zero — see `floor`. Clamped
    // below the optimal ceiling so a bad config cannot invert the green band,
    // and below the value so an impossibly low result still plots.
    const floor = Math.min(cfg.floor ?? 0, optHi, value);
    return {
      domLo: floor,
      domHi,
      segments: [seg('green', floor, optHi), seg('yellow', optHi, refHi), seg('orange', refHi, sevHi), seg('red', sevHi, domHi)],
      ticks: [{ value: optHi, kind: 'opt' }, { value: refHi, kind: 'lab' }],
    };
  }
  if (cfg.direction === 'higherBetter') {
    const domLo = Math.min(sevLo, value) * 0.9;
    const domHi = Math.max(optHi, refHi, value) * 1.07;
    return {
      domLo,
      domHi,
      segments: [seg('red', domLo, sevLo), seg('orange', sevLo, refLo), seg('yellow', refLo, optLo), seg('green', optLo, domHi)],
      ticks: [{ value: refLo, kind: 'lab' }, { value: optLo, kind: 'opt' }],
    };
  }
  // bandOptimal — symmetric, optimal band in the middle.
  const lo = Math.min(sevLo, value);
  const hi = Math.max(sevHi, value);
  const pad = (hi - lo) * 0.05;
  const domLo = lo - pad;
  const domHi = hi + pad;
  return {
    domLo,
    domHi,
    segments: [
      seg('red', domLo, sevLo),
      seg('orange', sevLo, refLo),
      seg('yellow', refLo, optLo),
      seg('green', optLo, optHi),
      seg('yellow', optHi, refHi),
      seg('orange', refHi, sevHi),
      seg('red', sevHi, domHi),
    ],
    ticks: [
      { value: refLo, kind: 'lab' },
      { value: optLo, kind: 'opt' },
      { value: optHi, kind: 'opt' },
      { value: refHi, kind: 'lab' },
    ],
  };
}

/**
 * Zone bands for an index bar (green→red around the decision limit). `domMax` is
 * the top of the bar's scale. None of the scores are capped at 100 — the MVX
 * headline and the IVX/MMX sub-scores have all been observed above 100 — so the
 * hero gauge and the panel bars pass a `domMax` wide enough to keep an
 * out-of-range value on-scale, and the red band runs out to that top.
 */
export function indexSegments(cfg: MvxIndexConfig, specimen: Specimen = 'plasma', domMax = 100): MvxBarSegment[] {
  const limit = indexLimit(cfg, specimen);
  const green = Math.max(0, cfg.greenThreshold);
  const redStart = Math.min(domMax, cfg.alarmFrom ?? limit + INDEX_RED_MARGIN);
  // The bar starts at the index's FLOOR, not at zero. MMX cannot go below 36
  // — Dr. Schmidt, 2026-10-08: "I have never seen one below 36" — and a bar
  // drawn from 0 spent a quarter of its green on scores that do not occur,
  // which squashed the part that does and made the gradations meaningless
  // (0, 9.8, 19.6, 29.4, 39.2, 49 for a marker that lives between 36 and 49).
  // MVX and IVX have a floor of 0, so nothing changes for them.
  //
  // DISPLAY ONLY, like softenFrom on GlycA: indexZone still compares against
  // greenThreshold and the DB range row keeps its 0 low bounds, so a score
  // below the floor is not newly flagged — it just pins to the left edge.
  const floor = Math.max(0, Math.min(cfg.floor ?? 0, green));
  return [
    { zone: 'green', from: floor, to: green },
    { zone: 'yellow', from: green, to: limit },
    { zone: 'orange', from: limit, to: redStart },
    { zone: 'red', from: redStart, to: domMax },
  ];
}

/**
 * The numbers PRINTED at an index bar's band edges: for each band, the last
 * score still inside it.
 *
 * `indexSegments` returns GEOMETRY, and its edges are the first score OUTSIDE
 * each band, because `indexZone` compares strictly (green while `value <
 * greenThreshold`). Printing those raw gave MVX "35 · 63 · 78" under bands that
 * actually end at 34, 62 and 77 — the same confusion Labcorp's own "<63"
 * causes, one layer down.
 *
 * Every other bar in the app prints the last in-band value: GlycA's edges read
 * 260 · 399 · 700, which ARE its optimal max, lab max and alarm anchor. This
 * makes the three index bars say it the same way, and makes the bar agree with
 * the Master Ranges Sheet, the DB rows and the Range References table.
 *
 * Display only. Geometry stays untouched, so the colored bands and the blood
 * drop still sit exactly where the zone function puts them.
 */
export function indexBoundaryLabels(segs: MvxBarSegment[]): number[] {
  return [segs[0].from, segs[0].to - 1, segs[1].to - 1, segs[2].to - 1, segs[3].to];
}

// ─────────────────────────── patterns ───────────────────────────

/*
 * POC categoryId block (900000–900099) — still confirm before DB upload.
 *
 * MOVED OFF 90000-90099 on 2026-09-02. That block is the Stress & Adrenal
 * family and every one of these six ids collided with a live adrenal pattern:
 * 90000 is Na/K high, 90010 Na/K low, 90020 cortisol low, 90030 evening
 * cortisol low, 90040 DHEA-S low. Nothing broke only because MVX is not wired
 * to the database yet; uploading would have overwritten shipped adrenal rows.
 *
 * 900000 continues the 100k-step convention the other libraries follow —
 * 500000 supplements, 600100 liver references, 700000 About descriptions,
 * 800000 range bibliography — and sits clear of all of them, the bibliography
 * being the highest in use at 801820.
 */
export enum MvxPatternId {
  Optimal = 900000,
  Inflammation = 900010,
  ToxicBurden = 900020,
  Mitochondrial = 900030,
  Malnutrition = 900040,
  CarbLoad = 900050,
  PossibleInfection = 900060,
  ExerciseArtifact = 900070,
}

export type MvxFactorStates = Record<MvxFactorId, MvxTri>;

export interface MvxPattern {
  id: MvxPatternId;
  key: string;
  /** Practitioner-facing name (clinical language allowed). */
  practitionerName: string;
  /** Client-facing title — observational, non-diagnostic. */
  clientTitle: string;
  /** Arrow row for the marker strip (mirrors the DB "Logic" column). */
  markerRow: string;
}

interface MvxPatternRule extends MvxPattern {
  /** Priority: lower fires first / is the dominant box. */
  priority: number;
  match: (s: MvxFactorStates) => boolean;
}

const bcaaKeys: MvxFactorId[] = ['valine', 'leucine', 'isoleucine'];
const anyBcaa = (s: MvxFactorStates, state: MvxTri): boolean => bcaaKeys.some(k => s[k] === state);

/**
 * The pre-analytic signature: leucine pulled out of the blood by muscle
 * breakdown while isoleucine rises because the system that caps circulating
 * amino acids has shut off (Dr. Schmidt, 2026-10-08).
 *
 * It SUPPRESSES the two BCAA readings, confirmed by Maryann 2026-10-08. Those
 * two fire off the same pair of values, so without this one artifactual draw
 * produced three bullets from a single cause, two of which describe the
 * client when the real subject is the specimen.
 */
const exerciseArtifact = (s: MvxFactorStates): boolean =>
  s.leucine === 'low' && s.isoleucine === 'high';

/**
 * Pattern rules in priority order. Not mutually exclusive: a client can carry an
 * IVX story (1–2) and an MMX story (3–5) at once, so `resolveMvxPatterns`
 * returns every match. Toxic-burden is suppressed when GlycA is high, because a
 * low small HDL-P is then better explained by active inflammation consuming it.
 */
const MVX_PATTERN_RULES: MvxPatternRule[] = [
  {
    id: MvxPatternId.Inflammation,
    key: 'inflammation',
    priority: 1,
    practitionerName: 'Inflammatory / infection burden',
    clientTitle: 'Elevated inflammatory activity',
    markerRow: '▲ GlycA',
    match: s => s.glycA === 'high',
  },
  {
    id: MvxPatternId.Mitochondrial,
    key: 'mitochondrial',
    priority: 2,
    practitionerName: 'Mitochondrial dysfunction / metabolic acidosis tendency',
    clientTitle: 'Reduced cellular-energy efficiency',
    markerRow: '▲ Citrate',
    match: s => s.citrate === 'high',
  },
  {
    id: MvxPatternId.CarbLoad,
    key: 'carbLoad',
    priority: 3,
    // Practitioner-facing may say the clinical term; client title must not.
    practitionerName: 'Reduced insulin sensitivity / impaired BCAA catabolism',
    clientTitle: 'Reduced carbohydrate tolerance',
    markerRow: '▲ Valine ▲ Leucine ▲ Isoleucine',
    match: s => anyBcaa(s, 'high') && !exerciseArtifact(s),
  },
  {
    id: MvxPatternId.Malnutrition,
    key: 'malnutrition',
    priority: 4,
    practitionerName: 'Metabolic malnutrition (protein-energy undernutrition)',
    clientTitle: 'Reduced amino-acid status',
    markerRow: '▼ Valine ▼ Leucine ▼ Isoleucine',
    match: s => anyBcaa(s, 'low') && !exerciseArtifact(s),
  },
  {
    // NEW 2026-10-08. Unconditional, unlike toxic burden below: a low small
    // HDL-P is the single best-evidenced finding on this panel — in 30,195
    // people the lowest percentile carried HR 2.31 for any infection and 3.23
    // for infection death (PMID 36537045) — and that holds whether or not
    // GlycA is also up.
    //
    // It also closes a gap. Toxic burden is suppressed when GlycA is high, so
    // until now a low small HDL-P alongside high inflammation produced NO
    // bullet for the marker at all, even while it led the triage line.
    //
    // It DOES double up with toxic burden when GlycA is normal, and that is
    // intended (Maryann, 2026-10-08): one low value, two readings. "Possible
    // infection" is the risk the literature attaches to the number itself;
    // "reduced detoxification capacity" is what Dr. Schmidt reads into it once
    // inflammation is not the explanation.
    id: MvxPatternId.PossibleInfection,
    key: 'possibleInfection',
    priority: 5,
    practitionerName: 'Infection risk associated with low small HDL particle number',
    clientTitle: 'Possible infection',
    markerRow: '▼ Small HDL-P',
    match: s => s.sHdlP === 'low',
  },
  {
    id: MvxPatternId.ToxicBurden,
    key: 'toxicBurden',
    priority: 6,
    practitionerName: 'Toxic burden / impaired detoxification',
    clientTitle: 'Reduced detoxification capacity',
    markerRow: '▼ Small HDL-P',
    match: s => s.sHdlP === 'low' && s.glycA !== 'high',
  },
  {
    // NEW 2026-10-08, and NOT a finding about the client — a caveat about the
    // SPECIMEN. Dr. Schmidt: muscle breakdown pulls leucine out of the blood
    // while the system that normally caps circulating amino acids shuts off,
    // so isoleucine rises. He puts the effect at up to 10 MVX points.
    //
    // It SUPPRESSES CarbLoad and Malnutrition (confirmed 2026-10-08), because
    // all three read the same two values and only this one names the cause.
    //
    // THE TWO BCAAs ARE THE WHOLE RULE. Her sheet once carried "▲ MVX ▲ MMX"
    // in the marker row as well, and asked him whether they were required.
    // Dr. Schmidt, 2026-10-13: "bcaa pattern alone is enough." So a raised
    // MVX and MMX are what usually travel with an artifactual draw rather
    // than part of the rule, and the marker row shows only the two arrows
    // that are.
    id: MvxPatternId.ExerciseArtifact,
    key: 'exerciseArtifact',
    priority: 7,
    practitionerName: 'Pre-analytic: muscle breakdown before the draw',
    clientTitle: 'Exercise or other muscle breakdown activity before blood draw',
    markerRow: '▼ Leucine ▲ Isoleucine',
    match: exerciseArtifact,
  },
];

/** The "all clear" pattern, returned only when nothing else fires. */
export const MVX_OPTIMAL_PATTERN: MvxPattern = {
  id: MvxPatternId.Optimal,
  key: 'optimal',
  practitionerName: 'Low metabolic vulnerability',
  clientTitle: 'Optimal metabolic resilience',
  markerRow: '',
};

/**
 * Resolve factor states to every matching pattern, highest priority first.
 * Returns the Optimal pattern when at least one factor was assessed and nothing
 * fired; returns `[]` when nothing was assessed at all.
 */
export function resolveMvxPatterns(s: MvxFactorStates): MvxPattern[] {
  const anyAssessed = MVX_MARKERS.some(m => s[m.id] !== 'absent');
  if (!anyAssessed) return [];

  const matched = MVX_PATTERN_RULES.filter(r => r.match(s))
    .sort((a, b) => a.priority - b.priority)
    .map(({ priority, match, ...pattern }) => pattern);

  return matched.length > 0 ? matched : [MVX_OPTIMAL_PATTERN];
}

// ─────────────────────────── triage (what to work on first) ───────────────────────────

/**
 * Where a factor sits, for Schmidt's "what do I treat first" rule.
 *  • 'labAbnormal' — outside the lab reference interval.
 *  • 'offTarget'   — inside the lab interval but outside his target band.
 *  • 'atTarget'    — inside the target band.
 */
export type MvxTriageTier = 'labAbnormal' | 'offTarget' | 'atTarget';

export interface MvxTriageEntry {
  id: MvxFactorId;
  short: string;
  tier: MvxTriageTier;
  /**
   * How far out, expressed in units of this marker's OWN lab reference width,
   * so six markers in four different scales can be ranked against each other.
   * 0.30 = three tenths of a reference interval past the limit. At target it
   * measures the distance from the ideal point instead, which is never 0 for
   * long and is what lets the rule still pick a winner when everything is good.
   *
   * COMPARABLE WITHIN A TIER ONLY. An at-target marker can carry a bigger
   * number than an off-target one (drift from an ideal point is measured over
   * the whole band, a miss only from its edge), which is why the sort puts
   * tier first and never ranks on severity alone.
   */
  severity: number;
  /** Which way it missed; at target, which edge it is drifting toward. */
  side: 'high' | 'low';
  /**
   * The marker's own direction. At target the wording depends on it, because
   * "the highest" only means "worst" on a lower-better marker.
   */
  direction: MvxDirection;
}

const TIER_RANK: Record<MvxTriageTier, number> = { labAbnormal: 0, offTarget: 1, atTarget: 2 };

/**
 * How the panel words each tier: "<marker> — <reason>."
 *
 * Here rather than in the component because the sheet quotes it too (see
 * scripts/generateMvxStartWithTab.ts), and a rule Dr. Schmidt is reviewing has
 * to be the rule the practitioner sees.
 */
export const TRIAGE_REASON: Record<Exclude<MvxTriageTier, 'atTarget'>, string> = {
  labAbnormal: 'furthest outside its lab range',
  offTarget: 'furthest from its target range',
};

/**
 * The at-target wording, which is his rule 3 and his own example.
 *
 * Slide 70 of the NEXGEN deck: "If all numbers are excellent, still find the
 * one 'most out of range'. In this example, GlycA is the highest. He eats
 * sweets and his teeth get infected." So on an all-excellent panel he does
 * name one, and he names it by where it sits inside its own range.
 *
 * Per DIRECTION, because "the highest" is only the worst end on a
 * lower-better marker. An earlier single phrase, "closest to leaving its
 * target range", was wrong twice over: it is not what the ranking measures
 * (drift from each marker's own ideal point), and on a GlycA of 220 against a
 * target of 260 it was not true either.
 */
export const AT_TARGET_REASON: Record<MvxDirection, string> = {
  lowerBetter: 'the highest within its target range',
  // ⚠ UNREACHABLE for the six ranked factors as they stand: none is
  // higherBetter since Small HDL-P became a window on 2026-10-07, and it was
  // the only one. Kept because the Record is keyed by direction and a factor
  // could go back. The Start With tab no longer quotes it as a phrase a
  // practitioner can see (scripts/generateMvxStartWithTab.ts).
  higherBetter: 'the lowest within its target range',
  bandOptimal: 'furthest from the middle of its target range',
};

/** The words that follow a marker's name on the "Start with" line. */
export function triageReason(entry: MvxTriageEntry): string {
  return entry.tier === 'atTarget'
    ? AT_TARGET_REASON[entry.direction]
    : TRIAGE_REASON[entry.tier];
}

/**
 * Darren Schmidt's triage rule, from the "HOW TO READ MVX Plus" slides of his
 * 2026-09-20 NEXGEN talk (docs/reference/mvx-schmidt-slides-2026-09-20.md):
 *
 *   1. "If a number is 'lab high' start there."
 *   2. "If all numbers are normal ranges, pick the one 'most out of range'."
 *   3. "If all numbers are excellent, still find the one 'most out of range'."
 *
 * So: tier first, then distance within the tier. Returned worst-first, so
 * `[0]` is where he would start.
 *
 * A MARKER CAN ALSO BE LEFT OUT BY DIRECTION. `triageSide` names the one side
 * of a band that is a place to start; a miss on the other side is real, draws
 * on the bar and prints its value, but is never nominated here. Small HDL-P is
 * the only factor that uses it, and only on the high side, where Dr. Schmidt
 * has no starting move to give. See its entry in MVX_MARKERS.
 *
 * ONLY THE SIX MEASURED FACTORS ARE RANKED. The three index scores are left out
 * deliberately — "There is no 'treating' the MVX, IVX. They are a guide" (his
 * p65). Ranking a score you cannot treat would point the practitioner at a
 * number instead of at a cause.
 *
 * Normalizing by the lab reference width is what makes the comparison fair.
 * Normalizing by the gap between the target edge and the lab edge was tried and
 * is unstable: leucine's target ceiling (170) sits 9 units under its lab ceiling
 * (179), so a trivial miss there would outrank a GlycA of 520.
 */
export function resolveMvxTriage(factors: MvxFactorResult[]): MvxTriageEntry[] {
  const entries: MvxTriageEntry[] = [];

  factors.filter(f => f.hasValue).forEach(f => {
    const [optLo, optHi] = targetBand(f);
    const [refLo, refHi] = f.reference;
    const width = refHi - refLo || 1; // guard a degenerate range rather than divide by zero
    const value = f.value;
    const penalizeHigh = f.direction !== 'higherBetter';
    const penalizeLow = f.direction !== 'lowerBetter';
    /**
     * Push an entry unless this marker's unactionable side produced it.
     *
     * A skipped marker gets NO entry at any tier, which is why every caller
     * below returns through here: falling through to the at-target branches
     * would file a genuinely high value as "at target" and rank it on drift
     * from an ideal point it is nowhere near. Better to say nothing than to
     * say the wrong thing quietly.
     *
     * The gate covers the at-target tier too. A band marker sitting high
     * inside its own green is not a problem to solve either, so "most out of
     * range" pointing at it would be the same unactionable nudge in a
     * politer tier.
     */
    const add = (tier: MvxTriageTier, distance: number, side: 'high' | 'low') => {
      if (f.triageSide && f.triageSide !== side) return;
      entries.push({
        id: f.id,
        short: f.short,
        tier,
        severity: Math.max(0, distance) / width,
        side,
        direction: f.direction,
      });
    };

    if (penalizeHigh && value > refHi) return add('labAbnormal', value - refHi, 'high');
    if (penalizeLow && value < refLo) return add('labAbnormal', refLo - value, 'low');
    if (penalizeHigh && value > optHi) return add('offTarget', value - optHi, 'high');
    if (penalizeLow && value < optLo) return add('offTarget', optLo - value, 'low');

    // At target. Rank by the drift from this marker's ideal point so rule 3
    // still has something to sort on: the bottom of the band when lower is
    // better, the top when higher is better, the middle for a window.
    if (f.direction === 'lowerBetter') return add('atTarget', value - optLo, 'high');
    if (f.direction === 'higherBetter') return add('atTarget', optHi - value, 'low');
    const mid = (optLo + optHi) / 2;
    return add('atTarget', Math.abs(value - mid), value >= mid ? 'high' : 'low');
  });

  return entries.sort((a, b) => TIER_RANK[a.tier] - TIER_RANK[b.tier] || b.severity - a.severity);
}

// ─────────────────────────── entry point ───────────────────────────

/**
 * Raw inputs. The six factors are lab values; the three indices are HELD from
 * the report (proprietary — not derivable here). Everything is optional so the
 * panel can render partial results as the practitioner enters them.
 */
export interface MvxInputs {
  glycA?: number;
  sHdlP?: number;
  citrate?: number;
  valine?: number;
  leucine?: number;
  isoleucine?: number;
  mvx?: number;
  ivx?: number;
  mmx?: number;
}

export interface MvxFactorResult extends MvxMarkerConfig {
  value: number;
  hasValue: boolean;
  state: MvxTri;
  zone: MvxZone;
}

export interface MvxIndexResult extends MvxIndexConfig {
  value: number;
  hasValue: boolean;
  zone: MvxZone;
}

export interface MvxAnalysis {
  factors: MvxFactorResult[];
  indices: MvxIndexResult[];
  states: MvxFactorStates;
  patterns: MvxPattern[];
  /** The six factors ranked by Schmidt's triage rule, worst first. */
  triage: MvxTriageEntry[];
  /** Highest-priority pattern for the hero box, or undefined if none assessed. */
  dominantPattern?: MvxPattern;
  anyEntered: boolean;
}

/** One-call entry point a panel would use — mirrors `computeI3`. */
export function analyzeMvx(inputs: MvxInputs | undefined, specimen: Specimen = 'plasma'): MvxAnalysis {
  let anyEntered = false;

  const factors: MvxFactorResult[] = MVX_MARKERS.map(cfg => {
    const { value, hasValue } = readNumber(inputs, cfg.id);
    if (hasValue) anyEntered = true;
    return {
      ...cfg,
      value,
      hasValue,
      state: hasValue ? markerState(value, cfg) : 'absent',
      zone: hasValue ? markerZone(value, cfg) : 'green',
    };
  });

  const indices: MvxIndexResult[] = MVX_INDICES.map(cfg => {
    const { value, hasValue } = readNumber(inputs, cfg.id);
    if (hasValue) anyEntered = true;
    return { ...cfg, value, hasValue, zone: hasValue ? indexZone(value, cfg, specimen) : 'green' };
  });

  const states = factors.reduce(
    (acc, f) => ({ ...acc, [f.id]: f.state }),
    {} as MvxFactorStates,
  );
  const patterns = resolveMvxPatterns(states);
  const triage = resolveMvxTriage(factors);

  return { factors, indices, states, patterns, triage, dominantPattern: patterns[0], anyEntered };
}
